import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';

const RECRUIT_TAGS = [
  'Gig & Festival Staffing',
  'Freelance & Contract',
  'Curation & Collections',
  'Gallery & Museum Management',
  'Marketing & Communications',
  'Events & Production',
  'Design & Creative Direction',
  'Education & Outreach',
  'Digital & Content',
  'Fundraising & Development',
  'Operations & Administration',
  'Visitor Experience & Front of House'
];

const ORG_TYPES = ['Gallery', 'Museum', 'Festival', 'Arts NGO', 'Creative Agency', 'Other'];
const TIMELINES = [
  'Within 1 week',
  'Within two weeks',
  'Within a month',
  'Within three months',
  'Just exploring'
];

const Kicker = ({ children, color = '#D4820A' }) => (
  <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color }}>
    {children}
  </p>
);

const SectionH2 = ({ children, light }) => (
  <h2
    className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight leading-tight"
    style={{
      fontFamily: 'Cormorant Garamond, serif',
      color: light ? '#FFFFFF' : '#0F2942'
    }}
  >
    {children}
  </h2>
);

const Paragraph = ({ children, light }) => (
  <p
    className="text-base md:text-lg leading-relaxed"
    style={{ color: light ? 'rgba(240, 235, 226, 0.72)' : '#0F2942' }}
  >
    {children}
  </p>
);

const scrollToForm = (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById('hire-form');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const HireCTA = ({ label = 'Hire through us', testid, variant = 'solid' }) => {
  const isSolid = variant === 'solid';
  return (
    <a
      href="#hire-form"
      onClick={scrollToForm}
      className="inline-flex items-center px-7 py-3.5 text-sm font-medium rounded-lg transition-all hover:opacity-90"
      style={
        isSolid
          ? { backgroundColor: '#D4820A', color: '#0F2942' }
          : { border: '1.5px solid #0F2942', color: '#0F2942', backgroundColor: 'transparent' }
      }
      data-testid={testid || 'hire-cta-jump'}
    >
      {label}
      <svg className="w-4 h-4 ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </a>
  );
};

const HireThroughUsPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [form, setForm] = useState({
    name: '',
    organisation: '',
    organisation_type: '',
    role: '',
    timeline: '',
    email: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organisation || !form.role || !form.organisation_type || !form.timeline || !form.phone) {
      toast.error('Please complete all fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/inquiries`, {
        name: form.name,
        email: form.email,
        inquiry_type: 'hire-through-us',
        message: `Role: ${form.role}\nOrganisation: ${form.organisation} (${form.organisation_type})\nTimeline: ${form.timeline}\nPhone: ${form.phone}`,
        organisation: form.organisation,
        organisation_type: form.organisation_type,
        role: form.role,
        timeline: form.timeline,
        phone: form.phone
      });
      setSubmitted(true);
      toast.success('Enquiry sent. We\'ll be in touch within 2 working days.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <Navbar />
      <SEO
        title="Hire Creative Talent in India"
        path="/hire"
        description="Hire vetted curators, designers, producers and cultural pros. Post a role on CultureCareers, India's specialist recruitment for the arts sector."
      />

      {/* 1. Hero */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="hire-hero">
        <div className="max-w-4xl">
          <span
            className="inline-block text-xs uppercase tracking-[0.2em] font-semibold mb-10"
            style={{ color: '#D4820A' }}
          >
            SPECIALIST RECRUITMENT FOR THE CULTURAL SECTOR
          </span>
          <h1
            className="font-normal tracking-tight mb-8"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: '#0F2942',
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.01em'
            }}
          >
            We find the right people for your{' '}
            <span style={{ color: '#D4820A', fontStyle: 'italic' }}>organisation</span>.
          </h1>
          <p className="text-base md:text-lg leading-relaxed font-light max-w-2xl" style={{ color: '#0F2942' }}>
            India&rsquo;s only specialist recruitment agency for arts and cultural organisations. Tell us who you need. We&rsquo;ll handle the rest.
          </p>
          <div className="mt-10">
            <HireCTA label="Start a hire" testid="hire-cta-hero" />
          </div>
        </div>
      </section>

      {/* 2. How it works */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24" style={{ backgroundColor: '#F0EBE2' }} data-testid="hire-how">
        <div className="max-w-6xl mx-auto">
          <Kicker>How it works</Kicker>
          <SectionH2>Three steps.</SectionH2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 mt-14">
            {[
              {
                step: 'Step 1',
                title: 'Brief us',
                body: "You tell us about the role, the team, and what good looks like. We ask the questions a generalist recruiter wouldn't think to ask."
              },
              {
                step: 'Step 2',
                title: 'We search',
                body: 'We draw from our active talent network and reach out directly to relevant candidates. You receive three to five vetted, relevant people. Not forty CVs.'
              },
              {
                step: 'Step 3',
                title: 'You hire',
                body: 'You meet the shortlist. You make the call. We handle offer coordination.'
              }
            ].map((s, i) => (
              <div
                key={s.step}
                className="pt-6"
                style={{ borderTop: '2px solid #D4820A' }}
                data-testid={`hire-step-${i + 1}`}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-4" style={{ color: '#D4820A' }}>
                  {s.step}
                </p>
                <h3
                  className="text-2xl md:text-3xl font-normal tracking-tight mb-4"
                  style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
                >
                  {s.title}
                </h3>
                <Paragraph>{s.body}</Paragraph>
              </div>
            ))}
          </div>

          <div className="mt-14">
            <HireCTA label="Hire through us" testid="hire-cta-how" />
          </div>
        </div>
      </section>

      {/* 3. What we recruit for */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24" data-testid="hire-roles">
        <div className="max-w-5xl">
          <Kicker>What we recruit for</Kicker>
          <SectionH2>Every creative role you can imagine.</SectionH2>
          <p className="text-xl md:text-2xl font-light mt-3 mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
            Full-time, freelance, or a short gig.
          </p>
          <div className="max-w-3xl">
            <Paragraph>
              Whether you need a curator, a communications manager, a muralist from a specific community, or someone to work a music festival this weekend. If it&rsquo;s a creative or cultural role, we can find the right person.
            </Paragraph>
          </div>

          <div className="flex flex-wrap gap-2.5 mt-10">
            {RECRUIT_TAGS.map((t) => (
              <span
                key={t}
                className="text-sm rounded-full px-4 py-2"
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #F0EBE2',
                  color: '#0F2942'
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 3.5 Reasons to choose CultureCareers */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28" style={{ backgroundColor: '#F0EBE2' }} data-testid="hire-reasons">
        <div className="max-w-6xl mx-auto">
          <Kicker>Why CultureCareers</Kicker>
          <SectionH2>Creative recruitment, done right.</SectionH2>
          <p className="text-base md:text-lg leading-relaxed font-light max-w-3xl mt-6 mb-14" style={{ color: '#0F2942' }}>
            Hiring a curator is not the same as hiring an accountant. We know the difference, and we&rsquo;ve built our search around it.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {[
              {
                n: '01',
                t: 'Targeted to culture',
                b: 'We reach candidates actively looking for roles in the arts, music, festivals, heritage and museums. No generic job-board noise.'
              },
              {
                n: '02',
                t: 'Trusted by employers',
                b: 'From government bodies to private galleries and national institutions, leading cultural organisations across India post with us.'
              },
              {
                n: '03',
                t: 'High visibility',
                b: 'Listings surface on CultureCareers, Instagram, LinkedIn and Google Jobs. Featured roles get priority homepage placement and direct email distribution.'
              },
              {
                n: '04',
                t: 'Affordable',
                b: 'Whether you post a single role or recruit through us, our pricing fits cultural-sector budgets. Targeted results, shortlists tailored to your organisation.'
              },
              {
                n: '05',
                t: 'Quick and simple',
                b: 'From posting a role to receiving qualified candidates, the entire process is built to move at the pace of your hire.'
              }
            ].map((r) => (
              <div key={r.n} className="flex gap-5" data-testid={`hire-reason-${r.n}`}>
                <p
                  className="text-sm font-semibold shrink-0 pt-1"
                  style={{ color: '#D4820A', fontFamily: "'Playfair Display', serif", letterSpacing: '0.02em' }}
                >
                  {r.n}
                </p>
                <div>
                  <h3
                    className="text-xl md:text-2xl font-normal tracking-tight mb-2.5"
                    style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
                  >
                    {r.t}
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: '#0F2942' }}>
                    {r.b}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Closing manifesto line */}
          <div className="mt-16 md:mt-20 max-w-3xl border-l-2 pl-6 md:pl-8" style={{ borderColor: '#D4820A' }}>
            <p
              className="font-normal tracking-tight"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: '#0F2942',
                fontSize: 'clamp(1.35rem, 2.4vw, 1.75rem)',
                lineHeight: 1.3
              }}
            >
              Whether you&rsquo;re hiring a curator, a marketing manager or a senior leader, CultureCareers connects the{' '}
              <span style={{ color: '#D4820A', fontStyle: 'italic' }}>right organisations</span>{' '}
              with the{' '}
              <span style={{ color: '#D4820A', fontStyle: 'italic' }}>right people</span>{' '}
              in India&rsquo;s cultural sector.
            </p>
          </div>
        </div>
      </section>

      {/* 4. How we charge */}
      <section className="relative px-6 md:px-12 lg:px-24 py-20 md:py-28 overflow-hidden" style={{ backgroundColor: '#0F2942' }} data-testid="hire-pricing">
        {/* Warm photo backdrop on the right */}
        <div className="hidden md:block absolute right-0 top-0 bottom-0 w-1/3 opacity-25" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1772987020530-4ceac482c95a?w=900&q=60&auto=format&fit=crop"
            alt=""
            loading="lazy"
            decoding="async"
            width="900"
            height="900"
            className="w-full h-full object-cover"
            style={{ maskImage: 'linear-gradient(to left, black, transparent)', WebkitMaskImage: 'linear-gradient(to left, black, transparent)' }}
          />
        </div>
        <div className="relative max-w-3xl">
          <Kicker color="#D4820A">How we charge</Kicker>
          <SectionH2 light>Transparent from the start.</SectionH2>
          <div className="mt-6 space-y-5">
            <Paragraph light>
              We work on a placement fee model. For freelance and project-based roles, we charge a fixed sourcing fee.
            </Paragraph>
            <Paragraph light>
              No surprises, no hidden costs. We&rsquo;ll give you a clear number in our first conversation.
            </Paragraph>
          </div>
        </div>
      </section>

      {/* 5. Enquiry form */}
      <section id="hire-form" className="px-6 md:px-12 lg:px-24 py-20 md:py-24" data-testid="hire-form">
        <div className="max-w-3xl mx-auto">
          <Kicker>Enquiry</Kicker>
          <SectionH2>Tell us about the role.</SectionH2>
          <p className="text-base md:text-lg mt-3 mb-10" style={{ color: '#0F2942' }}>
            We respond within 2 working days.
          </p>

          {submitted ? (
            <div className="bg-white rounded-xl p-8 md:p-10 shadow-sm text-center" data-testid="hire-success">
              <h3 className="text-2xl font-normal tracking-tight mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
                Thank you.
              </h3>
              <p style={{ color: '#0F2942' }}>
                Your enquiry is in. We&rsquo;ll be in touch within 2 working days.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-xl p-8 md:p-10 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium mb-2 block">Full name *</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="Your full name"
                    data-testid="hire-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Organisation name *</label>
                  <Input
                    value={form.organisation}
                    onChange={(e) => setForm({ ...form, organisation: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="Your organisation"
                    data-testid="hire-org"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Organisation type *</label>
                  <Select value={form.organisation_type} onValueChange={(v) => setForm({ ...form, organisation_type: v })}>
                    <SelectTrigger className="rounded-lg" data-testid="hire-org-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ORG_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Role you&rsquo;re looking to fill *</label>
                  <Input
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="e.g. Curator, Producer, Designer"
                    data-testid="hire-role"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Timeline *</label>
                  <Select value={form.timeline} onValueChange={(v) => setForm({ ...form, timeline: v })}>
                    <SelectTrigger className="rounded-lg" data-testid="hire-timeline">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Email address *</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="you@example.com"
                    data-testid="hire-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium mb-2 block">Contact number *</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="+91 98765 43210"
                    data-testid="hire-phone"
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-lg px-8 py-3 font-medium text-white"
                  style={{ backgroundColor: '#D4820A' }}
                  data-testid="hire-submit"
                >
                  {submitting ? 'Sending…' : 'Send enquiry'}
                </Button>
                <p className="text-xs mt-4" style={{ color: '#0F2942' }}>
                  We do not share your details with third parties.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HireThroughUsPage;
